import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  useTheme,
  alpha,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useMediaQuery
} from '@mui/material';
import {
  Message as SmsIcon,
  Event as EventIcon,
  PersonAdd as PersonAddIcon,
  TrendingUp as TrendingUpIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Error as ErrorIcon,
  Warning as BounceIcon,
  Send as SendIcon,
  Build as BuildIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

import { apiService } from '../services/api.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import StatsCard from '../components/dashboard/StatsCard.jsx';
import DetailBreakdown from '../components/dashboard/DetailBreakdown.jsx';
import DateRangePicker from '../components/dashboard/DateRangePicker.jsx';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardPage = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [period, setPeriod] = useState('month');
  const [dashboardData, setDashboardData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [selectedMetric, setSelectedMetric] = useState('appointments');
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCard, setExpandedCard] = useState('appointments');
  const [mobileTab, setMobileTab] = useState(0);
  const [customDateRange, setCustomDateRange] = useState(null);

  useEffect(() => {
    fetchDashboardData();
  }, [period, customDateRange]);

  useEffect(() => {
    if (selectedMetric) {
      fetchChartData(selectedMetric);
    }
  }, [selectedMetric, period, customDateRange]);

  const getDateParams = () => {
    const today = new Date();
    
    if (period === 'custom' && customDateRange) {
      return {
        start_date: customDateRange.startDate.toLocaleDateString('en-CA'), // YYYY-MM-DD format
        end_date: customDateRange.endDate.toLocaleDateString('en-CA')
      };
    } else if (period === 'today') {
      const dateStr = today.toLocaleDateString('en-CA');
      return { start_date: dateStr, end_date: dateStr };
    } else if (period === 'week') {
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Start of week (Sunday)
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // End of week
      return {
        start_date: startOfWeek.toLocaleDateString('en-CA'),
        end_date: endOfWeek.toLocaleDateString('en-CA')
      };
    } else if (period === 'month') {
      const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      return {
        start_date: startOfMonth.toLocaleDateString('en-CA'),
        end_date: endOfMonth.toLocaleDateString('en-CA')
      };
    } else if (period === 'year') {
      const startOfYear = new Date(today.getFullYear(), 0, 1);
      return {
        start_date: startOfYear.toLocaleDateString('en-CA'),
        end_date: today.toLocaleDateString('en-CA')
      };
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const dateParams = getDateParams();
      const url = `/dashboard/stats?start_date=${dateParams.start_date}&end_date=${dateParams.end_date}`;
      
      const response = await apiService.get(url);
      setDashboardData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error('Dashboard data error:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async (metric) => {
    try {
      setChartLoading(true);
      const dateParams = getDateParams();
      const url = `/dashboard/chart-data/${metric}?start_date=${dateParams.start_date}&end_date=${dateParams.end_date}&period=${period}`;
      
      const response = await apiService.get(url);
      setChartData(response.data);
    } catch (err) {
      console.error('Chart data error:', err);
    } finally {
      setChartLoading(false);
    }
  };

  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
    setCustomDateRange(null);
  };

  const handleCustomRangeChange = (dateRange) => {
    setPeriod('custom');
    setCustomDateRange(dateRange);
  };

  const handleCardClick = (cardType) => {
    setSelectedMetric(cardType);
    setExpandedCard(cardType);
    // Sync mobile tab with selected metric
    const metricMap = ['appointments', 'sms', 'customers'];
    const tabIndex = metricMap.indexOf(cardType);
    if (tabIndex !== -1) {
      setMobileTab(tabIndex);
    }
  };

  const handleMobileTabChange = (event, newValue) => {
    if (newValue !== null) { // Prevent null value when clicking the same tab
      setMobileTab(newValue);
      const metricMap = ['appointments', 'sms', 'customers'];
      setSelectedMetric(metricMap[newValue]);
      setExpandedCard(metricMap[newValue]);
    }
  };

  const getBreakdownIcon = (type, key) => {
    if (type === 'sms') {
      switch (key) {
        case 'MOT Reminder': return <ScheduleIcon sx={{ color: theme.palette.warning.main }} />;
        case 'Booking Confirmation': return <CheckCircleIcon sx={{ color: theme.palette.success.main }} />;
        case 'Booking Cancellation': return <CancelIcon sx={{ color: theme.palette.error.main }} />;
        case 'Completion': return <CheckCircleIcon sx={{ color: theme.palette.info.main }} />;
        default: return <SendIcon />;
      }
    } else if (type === 'appointments') {
      // Use BuildIcon for all appointment service types
      return <BuildIcon sx={{ color: theme.palette.primary.main }} />;
    } else if (type === 'customers') {
      switch (key) {
        case 'Online': return <PersonAddIcon sx={{ color: theme.palette.success.main }} />;
        case 'Admin Created': return <PersonAddIcon sx={{ color: theme.palette.info.main }} />;
        default: return <PersonAddIcon />;
      }
    }
    return null;
  };

  
  const getChartOptions = () => ({
    responsive: true,
    maintainAspectRatio: false,
    resizeDelay: 0,
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: theme.palette.mode === 'dark' ? '#333' : '#fff',
        titleColor: theme.palette.text.primary,
        bodyColor: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
        },
      },
      y: {
        grid: {
          display: false,
        },
        ticks: {
          color: theme.palette.text.secondary,
          stepSize: 1,
          callback: function(value) {
            if (Number.isInteger(value)) {
              return value;
            }
          }
        },
        beginAtZero: true,
      },
    },
    elements: {
      line: {
        tension: 0.4,
      },
      point: {
        radius: 4,
        hoverRadius: 6,
      },
    },
  });

  const getChartConfig = () => {
    if (!chartData) return null;

    return {
      labels: chartData.labels,
      datasets: [
        {
          label: selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1),
          data: chartData.data,
          borderColor: theme.palette.primary.main,
          backgroundColor: alpha(theme.palette.primary.main, 0.1),
          fill: true,
          borderWidth: 2,
        },
      ],
    };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  const getPeriodText = () => {
    switch (period) {
      case 'today':
        return 'today';
      case 'week':
        return 'this week';
      case 'month':
        return 'this month';
      case 'year':
        return 'this year';
      case 'custom':
        return 'selected period';
      default:
        return 'this month';
    }
  };

  const stats = [
    {
      id: 'appointments',
      title: 'Appointments',
      value: dashboardData?.appointment_stats?.total_appointments || 0,
      subtitle: `Appointments ${getPeriodText()}`,
      icon: EventIcon,
      color: theme.palette.success.main,
      breakdown: dashboardData?.appointment_stats?.breakdown
    },
    {
      id: 'sms',
      title: 'SMS Sent',
      value: dashboardData?.sms_stats?.total_sent || 0,
      subtitle: `SMS sent ${getPeriodText()}`,
      icon: SmsIcon,
      color: theme.palette.info.main,
      breakdown: dashboardData?.sms_stats?.breakdown
    },
    {
      id: 'customers',
      title: 'New Customers',
      value: dashboardData?.customer_stats?.new_customers || 0,
      subtitle: `New customers ${getPeriodText()}`,
      icon: PersonAddIcon,
      color: theme.palette.warning.main,
      breakdown: dashboardData?.customer_stats?.breakdown
    }
  ];

  // Function to get chart title with "Trend" suffix
  const getChartTitle = (metricId) => {
    const metric = stats.find(m => m.id === metricId);
    return metric ? `${metric.title} Trend` : `${metricId.charAt(0).toUpperCase() + metricId.slice(1)} Trend`;
  };

  return (
    <Container maxWidth="xl" sx={{ py: isMobile ? 2 : 4 }}>
      {/* Header */}
      <Box mb={4}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Overview of your garage's performance and statistics
        </Typography>
      </Box>

      {/* Date Range Picker */}
      <Box mb={4} display="flex" justifyContent="center">
        <DateRangePicker 
          period={period}
          onPeriodChange={handlePeriodChange}
          onCustomRangeChange={handleCustomRangeChange}
          customDateRange={customDateRange}
        />
      </Box>

      {/* Desktop Layout: Left column for count summary, Right column for chart */}
      {!isMobile ? (
        <Box display="flex" gap={3}>
          {/* Left Column - Count Summary */}
          <Box display="flex" flexDirection="column" gap={2} flex="0 0 400px">
            {stats.map((stat) => (
              <Card 
                key={stat.id}
                sx={{ 
                  cursor: 'pointer',
                  transition: 'all 0.2s ease-in-out',
                  border: selectedMetric === stat.id ? 
                    `2px solid ${theme.palette.primary.main}` : 
                    `1px solid ${theme.palette.divider}`,
                  '&:hover': {
                    boxShadow: theme.shadows[4],
                    transform: 'translateY(-2px)'
                  }
                }}
                onClick={() => handleCardClick(stat.id)}
              >
                <CardContent>
                  <Box display="flex" alignItems="center" mb={2}>
                    <stat.icon sx={{ 
                      fontSize: 32, 
                      color: stat.color, 
                      mr: 2 
                    }} />
                    <Box flex={1}>
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        {stat.title}
                      </Typography>
                      <Typography variant="h3" component="div" sx={{ fontWeight: 'bold' }}>
                        {stat.value}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {stat.subtitle}
                      </Typography>
                    </Box>
                  </Box>
                  
                  {/* Expanded Details */}
                  {expandedCard === stat.id && stat.breakdown && (
                    <Box>
                      <Divider sx={{ my: 2 }} />
                      <List dense>
                        {Object.entries(stat.breakdown).map(([type, count]) => (
                          <ListItem key={type} sx={{ px: 0, py: 0.5 }}>
                            <ListItemIcon sx={{ minWidth: 32 }}>
                              {getBreakdownIcon(stat.id, type)}
                            </ListItemIcon>
                            <ListItemText 
                              primary={type}
                              secondary={`${count} ${stat.id === 'sms' ? 'messages' : stat.id === 'appointments' ? 'bookings' : 'customers'}`}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                            <Chip 
                              label={count} 
                              size="small" 
                              sx={{ 
                                bgcolor: alpha(stat.color, 0.1),
                                color: stat.color,
                                fontWeight: 'bold'
                              }} 
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Right Column - Chart */}
          <Box flex={1}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ height: '100%' }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <TrendingUpIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                  <Typography variant="h6">
                    {getChartTitle(selectedMetric)}
                  </Typography>
                </Box>
                <Box height={500} width="100%">
                  {chartLoading ? (
                    <Box 
                      display="flex" 
                      flexDirection="column" 
                      alignItems="center" 
                      justifyContent="center" 
                      height="100%"
                      gap={2}
                    >
                      <CircularProgress size={60} />
                      <Typography variant="body1" color="text.secondary">
                        Generating insights...
                      </Typography>
                    </Box>
                  ) : chartData ? (
                    <Line 
                      data={getChartConfig()} 
                      options={getChartOptions()} 
                    />
                  ) : (
                    <Box 
                      display="flex" 
                      alignItems="center" 
                      justifyContent="center" 
                      height="100%"
                    >
                      <Typography variant="body2" color="text.secondary">
                        No data available
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Box>
      ) : (
        /* Mobile Layout - Tabbed Interface */
        <>
          {/* Tabs for Stats */}
          <Tabs
            value={mobileTab}
            onChange={handleMobileTabChange}
            variant="fullWidth"
            sx={{ 
              mb: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                color: 'text.secondary',
                minHeight: 48,
                fontSize: '0.875rem',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600
                },
                '&:hover': {
                  color: 'primary.main'
                }
              },
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: 1.5,
                backgroundColor: 'primary.main'
              }
            }}
            aria-label="mobile stats tabs"
          >
            {stats.map((stat, index) => (
              <Tab key={stat.id} label={stat.title} id={`mobile-tab-${index}`} aria-controls={`mobile-tabpanel-${index}`} />
            ))}
          </Tabs>

          {/* Stats Card Content */}
          {stats.map((stat, index) => (
            <Box
              key={stat.id}
              role="tabpanel"
              hidden={mobileTab !== index}
              id={`mobile-tabpanel-${index}`}
              aria-labelledby={`mobile-tab-${index}`}
              sx={{ mb: 4 }}
            >
              {mobileTab === index && (
                <Card
                  sx={{
                    width: '100%',
                    boxSizing: 'border-box',
                    transition: 'all 0.2s ease-in-out',
                    border: selectedMetric === stat.id
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    '&:hover': {
                      boxShadow: theme.shadows[4],
                      transform: 'translateY(-2px)',
                    },
                  }}
                  onClick={() => handleCardClick(stat.id)}
                >
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      minHeight: '140px',
                    }}
                  >
                    <Box display="flex" alignItems="center" mb={2}>
                      <stat.icon
                        sx={{
                          fontSize: 32,
                          color: stat.color,
                          mr: 2,
                        }}
                      />
                      <Box flex={1}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          {stat.title}
                        </Typography>
                        <Typography
                          variant="h3"
                          component="div"
                          sx={{ fontWeight: 'bold' }}
                        >
                          {stat.value}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {stat.subtitle}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Expanded Details */}
                    {expandedCard === stat.id && stat.breakdown && (
                      <Box>
                        <Divider sx={{ my: 2 }} />
                        <List dense>
                          {Object.entries(stat.breakdown).map(([type, count]) => (
                            <ListItem key={type} sx={{ px: 0, py: 0.5 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {getBreakdownIcon(stat.id, type)}
                              </ListItemIcon>
                              <ListItemText
                                primary={type}
                                secondary={`${count} ${stat.id === 'sms' ? 'messages' : stat.id === 'appointments' ? 'bookings' : 'customers'}`}
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                              />
                              <Chip
                                label={count}
                                size="small"
                                sx={{
                                  bgcolor: alpha(stat.color, 0.1),
                                  color: stat.color,
                                  fontWeight: 'bold',
                                }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
            </Box>
          ))}

          {/* Chart */}
          <Card sx={{ width: '100%' }}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <TrendingUpIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6">
                  {getChartTitle(selectedMetric)}
                </Typography>
              </Box>
              <Box height={400} width="100%">
                {chartLoading ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                    gap={2}
                  >
                    <CircularProgress size={60} />
                    <Typography variant="body1" color="text.secondary">
                      Generating insights...
                    </Typography>
                  </Box>
                ) : chartData ? (
                  <Line data={getChartConfig()} options={getChartOptions()} />
                ) : (
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="100%"
                  >
                    <Typography variant="body2" color="text.secondary">
                      No data available
                    </Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </>
      )}
    </Container>
  );
};

export default DashboardPage;